import { useApp } from '../store';
import { displayName, initials } from '../people';
import VendorTag from './VendorTag';
import { ROLE_LABEL, toRole } from '../permissions';
import { RequestToAdd } from './RequestAccess';

/*
 * PEOPLE AND ACCESS, AS A MEMBER SEES IT — #359, widened.
 *
 * A member cannot manage anyone, so the full screen (roles, removal, admins —
 * Admin.tsx) is not theirs. But they may ASK an admin to add a colleague to a
 * board they are on, which the row policy has always allowed (my_rank_on >= 3).
 * This is the door: a read-only roster of who is already here, so they can see
 * whether the person they want is in the workspace at all, and the ask itself.
 *
 * Nothing on this screen changes anything. Every control that does lives in
 * Admin.tsx, behind a manager-or-admin gate; App.tsx chooses between the two on
 * the workspace role, so a member never renders one.
 */
export default function MemberPeople() {
  const profiles = useApp((s) => s.profiles);
  const roles = useApp((s) => s.roles);
  const access = useApp((s) => s.access);
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const here = projects.find((p) => p.id === projectId);

  /* Everyone the member can already see — the workspace roster, read-only. The
     allowlist names them; a profile gives the name and the fallback is the
     address. Sorted by name so it reads like a team list, not a dump. */
  const emails = Array.from(
    new Set([...(access.emails ?? []), ...Object.keys(profiles)].map((e) => e.toLowerCase()))
  )
    .filter(Boolean)
    .sort((a, b) => displayName(a, profiles).localeCompare(displayName(b, profiles)));

  return (
    <div className="admin">
      {projectId && <RequestToAdd kind="person" boardId={projectId} />}

      <section className="panel">
        <h2>
          In your workspace {emails.length > 0 && <span className="count">{emails.length}</span>}
        </h2>
        <p className="hint">
          Who is already here — so you can see whether the person you want to add to
          {here ? ` ${here.name}` : ' this board'} is in the workspace. This list is read-only;
          adding and removing people is an admin's job.
        </p>
        {emails.length === 0 ? (
          <p className="hint">No one else is listed yet.</p>
        ) : (
          <div className="mp-roster">
            {emails.map((email) => {
              const name = displayName(email, profiles);
              const role = toRole(roles[email] ?? 'member');
              return (
                <div key={email} className="mp-person">
                  <span className="mp-av">{initials(email, profiles)}</span>
                  <span className="req-row-id">
                    <span className="req-row-name">
                      {name}
                      <VendorTag who={email} profiles={profiles} compact />
                    </span>
                    <span className="req-row-sub">{email}</span>
                  </span>
                  <span className={`role-tag role-${role}`}>{ROLE_LABEL[role]}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="hint mp-foot">
        Need an assistant instead? Ask for one on <strong>Settings → Assistants</strong>.
      </p>
    </div>
  );
}
